import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.tsx'
// import TestApp from './TestApp'
import './index.css'
import './styles/tour.css'
import { preloadCriticalResources, getAdaptiveLoadingStrategy } from './utils/lazyLoading'

// Import production diagnostics
import './utils/productionDiagnostics'

// Add global error handler for uncaught errors
window.addEventListener('error', (event) => {
  console.error('🚨 Global error:', event.error);
});

// Add handler for unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  console.error('🚨 Unhandled promise rejection:', event.reason);
});

console.log('🚀 Starting Thriftalyze application...');

try {
  // Preload critical resources immediately
  preloadCriticalResources();
  console.log('✅ Critical resources preloaded');

  // Set up adaptive loading strategy based on device capabilities
  const loadingStrategy = getAdaptiveLoadingStrategy();
  document.documentElement.setAttribute('data-loading-strategy', JSON.stringify(loadingStrategy));
  console.log('✅ Loading strategy configured:', loadingStrategy);
} catch (error) {
  console.error('❌ Error during initialization:', error);
}

// Add debug utilities to global scope in development
if (import.meta.env.DEV) {
  import('./utils/geminiDebug').then((geminiDebug) => {
    (window as any).geminiDebug = geminiDebug;
    console.log('🔧 Debug utilities loaded. Use geminiDebug.runGeminiDiagnostics() to test Gemini function.');
  }).catch(error => console.error('❌ Failed to load debug utilities:', error));

  // Add lazy loading debug utilities
  import('./utils/lazyLoading').then((lazyLoading) => {
    (window as any).lazyLoadingDebug = {
      preloadComponent: lazyLoading.preloadComponent,
      getAdaptiveLoadingStrategy: lazyLoading.getAdaptiveLoadingStrategy,
    };
    console.log('🚀 Lazy loading debug utilities loaded.');
  }).catch(error => console.error('❌ Failed to load lazy loading debug utilities:', error));
}

console.log('🎯 Attempting to render React app...');

try {
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Root element not found! Make sure there is a div with id="root" in your HTML.');
  }

  console.log('🔧 Root element found:', rootElement);

  const root = ReactDOM.createRoot(rootElement);
  
  // Add extra logging for production debugging
  console.log('🔧 Creating React root...');
  
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
  
  console.log('✅ React app rendered successfully');
} catch (error) {
  console.error('❌ Failed to render React app:', error);
  
  // Enhanced error details
  console.error('Error details:', {
    message: error instanceof Error ? error.message : 'Unknown error',
    stack: error instanceof Error ? error.stack : 'No stack trace',
    name: error instanceof Error ? error.name : 'Unknown error type'
  });
  
  // Fallback: show error message on screen
  const rootElement = document.getElementById('root');
  if (rootElement) {
    rootElement.innerHTML = `
      <div style="padding: 20px; color: red; font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto;">
        <h1>Application Error</h1>
        <p>Failed to load the application. Please check the console for details.</p>
        <p><strong>Error:</strong> ${error instanceof Error ? error.message : 'Unknown error'}</p>
        <p><strong>Timestamp:</strong> ${new Date().toISOString()}</p>
        <p><strong>User Agent:</strong> ${navigator.userAgent}</p>
        <p><strong>URL:</strong> ${window.location.href}</p>
        <button onclick="window.location.reload()" style="margin-top: 10px; padding: 10px 20px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer;">
          Reload Page
        </button>
      </div>
    `;
  }
}
